import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, AlertCircle, CheckCircle2, CreditCard, Building2, Copy, Clock, FileText, Mail } from 'lucide-react';
import { useCustomerPaymentMethods } from '@/hooks/useCustomerPaymentMethods';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { usePlatformBankDetails } from '@/hooks/usePlatformBankDetails';
import { usePlatformInvoicingSetting } from '@/hooks/usePlatformInvoicingSetting';
import { PaymentMethodSelector, STRIPE_INVOICE_ID } from '@/components/payments/PaymentMethodSelector';
import { PaymentMethodDisplay } from '@/components/payments/PaymentMethodDisplay';
import { formatCurrency } from '@/lib/types/job';
import { calculateCcProcessingFee } from '@/lib/paymentUtils';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface PaymentMethodSelectionModalProps {
  open: boolean;
  organizationId: string;
  amount: number; // cents - base PO amount
  taxAmountCents?: number; // cents - tax amount
  currentPaymentMethodId?: string;
  jobPublicId?: string; // For Traditional ACH reference
  onConfirm: (selectedPaymentMethodId: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function PaymentMethodSelectionModal({
  open,
  organizationId,
  amount,
  taxAmountCents = 0,
  currentPaymentMethodId,
  jobPublicId,
  onConfirm,
  onCancel,
  loading = false,
}: PaymentMethodSelectionModalProps) {
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(
    currentPaymentMethodId || ''
  );
  const [showChangeMethod, setShowChangeMethod] = useState(false);
  const [isAddingMethod, setIsAddingMethod] = useState(false);
  
  const { paymentMethods, isLoading: loadingMethods, refetch: refetchPaymentMethods } = useCustomerPaymentMethods(organizationId);
  const { data: ccFeeSetting } = usePlatformSetting('cc_processing_fee_percentage');
  const { data: bankDetails, isLoading: loadingBankDetails } = usePlatformBankDetails();
  const { data: invoicingEnabled } = usePlatformInvoicingSetting();

  // Handle return from Stripe Checkout - show toast and refetch when modal opens after redirect
  // The URL param detection and modal opening is handled by the parent page
  useEffect(() => {
    if (!open) return;
    
    // Check if we just returned from adding a payment method
    const urlParams = new URLSearchParams(window.location.search);
    const paymentMethodAdded = urlParams.get('payment_method_added');
    const paymentMethodCancelled = urlParams.get('payment_method_cancelled');
    
    if (paymentMethodAdded === 'true') {
      toast.success('Payment method added successfully');
      refetchPaymentMethods();
    } else if (paymentMethodCancelled === 'true') {
      toast.info('Payment method setup was cancelled');
      refetchPaymentMethods(); // Still refetch in case they want to try again
    }
    // Note: URL cleanup is handled by the parent page
  }, [open, refetchPaymentMethods]);

  // Calculate CC processing fee
  const ccFeePercentage = ccFeeSetting ? parseFloat(ccFeeSetting.setting_value) : 2.9;
  
  // Determine if selected method is a card, ACH, Traditional ACH, or Stripe Invoice
  const isTraditionalAch = selectedPaymentMethodId === 'traditional_ach';
  const isStripeInvoice = selectedPaymentMethodId === STRIPE_INVOICE_ID;
  const selectedMethod = (!isTraditionalAch && !isStripeInvoice)
    ? paymentMethods?.find(pm => pm.stripe_payment_method_id === selectedPaymentMethodId)
    : null;
  const isCardPayment = selectedMethod?.payment_method_type === 'card';
  const isAchPayment = selectedMethod?.payment_method_type === 'us_bank_account';
  
  // Calculate fees using pass-through formula - CC fee applies to base + tax
  const baseForFee = amount + taxAmountCents;
  const ccProcessingFeeCents = isCardPayment ? calculateCcProcessingFee(baseForFee, ccFeePercentage) : 0;
  const totalAmountCents = amount + taxAmountCents + ccProcessingFeeCents;

  // Update selected method when current method or payment methods change
  useEffect(() => {
    if (currentPaymentMethodId) {
      setSelectedPaymentMethodId(currentPaymentMethodId);
    } else if (paymentMethods && paymentMethods.length > 0) {
      // Auto-select default payment method
      const defaultMethod = paymentMethods.find(pm => pm.is_default);
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.stripe_payment_method_id);
      } else {
        setSelectedPaymentMethodId(paymentMethods[0].stripe_payment_method_id);
      }
    }
  }, [currentPaymentMethodId, paymentMethods]);

  const hasMultipleMethods = (paymentMethods?.length || 0) > 1;
  
  // Platform-level payment options
  const isTraditionalAchEnabled = bankDetails?.enabled ?? false;
  const isStripeInvoicingEnabled = invoicingEnabled ?? false;
  const hasPlatformPaymentOptions = isTraditionalAchEnabled || isStripeInvoicingEnabled;
  const showPaymentMethodOptions = hasMultipleMethods || hasPlatformPaymentOptions;
  
  // Auto-expand payment options if no saved methods but platform options available
  useEffect(() => {
    if (paymentMethods?.length === 0 && hasPlatformPaymentOptions) {
      setShowChangeMethod(true);
    }
  }, [paymentMethods, hasPlatformPaymentOptions]);

  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  const handleConfirm = () => {
    if (selectedPaymentMethodId) {
      onConfirm(selectedPaymentMethodId);
    }
  };

  // Handle adding a new payment method via Stripe Checkout
  const handleAddPaymentMethod = async () => {
    setIsAddingMethod(true);
    try {
      const { data, error } = await invokeEdgeFunction('create-payment-setup', {
        body: {
          organizationId,
          jobId: jobPublicId, // Optional - for tracking
          successUrl: `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}payment_method_added=true`,
          cancelUrl: `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}payment_method_cancelled=true`,
        },
        callerOperation: 'PaymentMethodSelectionModal.addMethod',
      });
      
      if (error) throw error;
      
      const result = data as { url?: string };
      if (result?.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating payment setup:', error);
      toast.error('Failed to initiate payment method setup. Please try again.');
      setIsAddingMethod(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && !loading && onCancel()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Confirm Payment Method</DialogTitle>
          <DialogDescription>
            Review and confirm the payment method for this transaction
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Amount Display with Fee Breakdown */}
          <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">PO Amount:</span>
              <span className="text-lg font-semibold text-foreground">
                {formatCurrency(amount)}
              </span>
            </div>
            
            {/* Sales Tax Line */}
            {taxAmountCents > 0 && (
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Sales Tax:</span>
                <span className="text-lg font-semibold text-foreground">
                  {formatCurrency(taxAmountCents)}
                </span>
              </div>
            )}
            
            {/* Credit Card Fee Line - Only show for card payments */}
            {isCardPayment && (
              <div className="flex items-baseline justify-between text-orange-600 dark:text-orange-400">
                <span className="text-sm flex items-center gap-1.5">
                  <CreditCard className="h-3.5 w-3.5" />
                  Credit Card Processing Fee ({ccFeePercentage}%):
                </span>
                <span className="text-lg font-semibold">
                  {formatCurrency(ccProcessingFeeCents)}
                </span>
              </div>
            )}
            
            {/* ACH / Traditional ACH / Invoice No Fee Line */}
            {(isAchPayment || isTraditionalAch || isStripeInvoice) && (
              <div className="flex items-baseline justify-between text-green-600 dark:text-green-400">
                <span className="text-sm flex items-center gap-1.5">
                  {isStripeInvoice ? <FileText className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                  Processing Fee:
                </span>
                <span className="text-lg font-semibold">
                  $0.00
                </span>
              </div>
            )}
            
            {/* Divider */}
            <div className="border-t border-border" />
            
            {/* Total */}
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-foreground">
                {isTraditionalAch ? 'Amount to Transfer:' : isStripeInvoice ? 'Invoice Amount:' : 'Total to be Charged:'}
              </span>
              <span className="text-2xl font-bold text-foreground">
                {formatCurrency(totalAmountCents)}
              </span>
            </div>
            
            {/* Fee Messaging */}
            {isCardPayment && (
              <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
                <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
                  <strong>Save on fees!</strong> ACH bank transfers have no processing fee.
                </AlertDescription>
              </Alert>
            )}
            
            {isAchPayment && (
              <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-700 dark:text-green-300 text-sm">
                  <strong>No processing fee</strong> with ACH bank transfer!
                </AlertDescription>
              </Alert>
            )}
            
            {isTraditionalAch && (
              <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-700 dark:text-green-300 text-sm">
                  <strong>No processing fee</strong> with Traditional ACH wire transfer!
                </AlertDescription>
              </Alert>
            )}
            
          </div>

          {/* Stripe Invoice Info Section */}
          {isStripeInvoice && (
            <div className="rounded-lg border-2 border-purple-300/50 bg-purple-50/50 dark:border-purple-800/50 dark:bg-purple-950/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                <h4 className="text-sm font-semibold text-foreground">Invoice Payment</h4>
              </div>
              
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>When you confirm, an invoice will be sent to your organization's accounts payable email address.</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Payment due upon receipt</li>
                  <li>Pay by card or bank transfer</li>
                  <li>Automatic reminders at 3 and 10 days if unpaid</li>
                </ul>
              </div>
              
              <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
                <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
                  The purchase order will be created after payment is received.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Traditional ACH Bank Details Section */}
          {isTraditionalAch && bankDetails && (
            <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Traditional ACH Transfer Details</h4>
              </div>
              
              <div className="space-y-3">
                {/* Bank Name */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Bank Name:</span>
                  <span className="font-medium">{bankDetails.bankName || 'Not configured'}</span>
                </div>
                
                {/* Routing Number */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Routing Number:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium">{bankDetails.routingNumber || 'Not configured'}</span>
                    {bankDetails.routingNumber && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(bankDetails.routingNumber, 'Routing number')}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Account Number */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Account Number:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium">{bankDetails.accountNumber || 'Not configured'}</span>
                    {bankDetails.accountNumber && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(bankDetails.accountNumber, 'Account number')}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Reference ID */}
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="text-sm text-muted-foreground">Reference ID:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-primary">{jobPublicId || 'N/A'}</span>
                    {jobPublicId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(jobPublicId, 'Reference ID')}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Settlement Time */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground border-t border-border pt-3">
                <Clock className="h-4 w-4" />
                <span>Expected Settlement: {bankDetails.settlementDays || '3-5'} business days after transfer</span>
              </div>
              
              {/* Important Notice */}
              <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
                  <strong>Important:</strong> Include the Reference ID <span className="font-mono font-bold">{jobPublicId}</span> in your transfer memo to avoid delays in processing.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Payment Method Section - Hide when Traditional ACH or Stripe Invoice is selected */}
          {!isTraditionalAch && !isStripeInvoice && (
            <div className="space-y-3">
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5">
                    <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div className="flex-1 space-y-1">
                    <h4 className="text-sm font-semibold text-foreground">Payment Method</h4>
                    <p className="text-sm text-muted-foreground">Will be charged to:</p>
                  </div>
                </div>

                {loadingMethods ? (
                  <div className="text-sm text-muted-foreground">Loading payment methods...</div>
                ) : !paymentMethods || paymentMethods.length === 0 ? (
                  hasPlatformPaymentOptions ? null : (
                    <div className="space-y-3">
                      <Alert>
                        <CreditCard className="h-4 w-4" />
                        <AlertDescription>
                          No saved payment methods. Add one below or choose an alternative payment option.
                        </AlertDescription>
                      </Alert>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleAddPaymentMethod}
                        disabled={isAddingMethod}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        {isAddingMethod ? 'Redirecting to Stripe...' : 'Add Payment Method'}
                      </Button>
                    </div>
                  )
                ) : selectedMethod ? (
                  <div className="ml-7">
                    <PaymentMethodDisplay 
                      paymentMethod={selectedMethod} 
                      showDefault={true}
                    />
                  </div>
                ) : null}

                {/* Change Payment Method Section */}
                {showPaymentMethodOptions && !loadingMethods && (
                  <Collapsible open={showChangeMethod} onOpenChange={setShowChangeMethod}>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-between text-sm font-medium"
                      >
                        Use Different Payment Method
                        {showChangeMethod ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-3">
                      <PaymentMethodSelector
                        organizationId={organizationId}
                        selectedPaymentMethodId={selectedPaymentMethodId}
                        onSelect={setSelectedPaymentMethodId}
                        onAddPaymentMethod={handleAddPaymentMethod}
                        isAddingPaymentMethod={isAddingMethod}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </div>
          )}

          {/* Show payment method selector when Traditional ACH or Stripe Invoice is selected to allow switching */}
          {(isTraditionalAch || isStripeInvoice) && (
            <Collapsible open={showChangeMethod} onOpenChange={setShowChangeMethod}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between text-sm font-medium"
                >
                  Use Different Payment Method
                  {showChangeMethod ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <PaymentMethodSelector
                  organizationId={organizationId}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onSelect={setSelectedPaymentMethodId}
                  onAddPaymentMethod={handleAddPaymentMethod}
                  isAddingPaymentMethod={isAddingMethod}
                />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !selectedPaymentMethodId || (loadingMethods && !isTraditionalAch && !isStripeInvoice) || (isTraditionalAch && loadingBankDetails)}
          >
            {loading 
              ? 'Processing...' 
              : isTraditionalAch 
                ? 'I Will Transfer Funds' 
                : isStripeInvoice 
                  ? 'Send Invoice' 
                  : 'Confirm Payment'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}